import {
  createAttractionTranslationsConfig,
  type AttractionTranslation,
} from '@/config/objects/attraction-translations';
import { useGenerateAttractionTranslations } from '@/hooks/useGenerateAttractionTranslations';
import { GenerateTranslationsDialog } from '@/components/shared/GenerateTranslationsDialog';
import { CrudListView } from '@startupquickstart/frontend';
import { Languages, Loader2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface AttractionTranslationsListProps {
  attractionId: string;
  className?: string;
}

/**
 * Displays all translations for an attraction using standard CrudListView
 */
export function AttractionTranslationsList({
  attractionId,
  className,
}: AttractionTranslationsListProps) {
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const generateMutation = useGenerateAttractionTranslations();
  const navigate = useNavigate();

  const handleGenerateTranslations = useCallback(() => {
    setGenerateDialogOpen(true);
  }, []);

  const translationsConfig = useMemo(
    () =>
      createAttractionTranslationsConfig(attractionId, {
        onGenerateTranslations: handleGenerateTranslations,
      }),
    [attractionId, handleGenerateTranslations],
  );

  const handleItemClick = useCallback(
    (translation: AttractionTranslation) => {
      navigate(`/attractions/${attractionId}/translations/${translation.id}`);
    },
    [attractionId, navigate],
  );

  return (
    <>
      <div className={className}>
        <CrudListView
          config={translationsConfig}
          baseUrl={`/attractions/${attractionId}/translations`}
          onItemClick={handleItemClick}
          getValueFromItem={(translation: AttractionTranslation) => translation.id}
          getLabelFromItem={(translation: AttractionTranslation) =>
            `${translation.language?.name || 'Unknown'} - ${translation.name}`
          }
          headerActions={[
            {
              key: 'generate',
              label: 'Generate Translations',
              icon: generateMutation.isPending ? Loader2 : Languages,
              variant: 'default' as const,
              onClick: () => setGenerateDialogOpen(true),
              loading: generateMutation.isPending,
            },
          ]}
        />
      </div>

      <GenerateTranslationsDialog
        open={generateDialogOpen}
        onClose={() => {
          setGenerateDialogOpen(false);
        }}
        title="Generate Translations"
        description="Select languages to translate this attraction to. Existing translations will be skipped."
        onGenerate={(selectedLanguageIds) => {
          generateMutation.mutate(
            {
              attractionId,
              targetLanguageIds: selectedLanguageIds,
            },
            {
              onSuccess: () => {
                setGenerateDialogOpen(false);
              },
            },
          );
        }}
        isGenerating={generateMutation.isPending}
      />
    </>
  );
}
